/**
 * British Columbia Property Transfer Tax (PTT) tiers and first-time buyer exemptions.
 * Used by the BC mortgage guide and regression tests.
 */

const BC_PTT_TIERS: { upTo: number; rate: number }[] = [
  { upTo: 200_000, rate: 0.01 },
  { upTo: 3_000_000, rate: 0.02 },
  { upTo: Infinity, rate: 0.03 },
];

/** Gross PTT before exemptions. Includes additional 2% residential surcharge above $3M. */
export function calculateBcPropertyTransferTax(
  purchasePrice: number,
  residential = true
): number {
  let tax = 0;
  let prev = 0;
  for (const tier of BC_PTT_TIERS) {
    const band = Math.min(purchasePrice, tier.upTo) - prev;
    if (band <= 0) break;
    tax += band * tier.rate;
    prev = tier.upTo;
    if (purchasePrice <= tier.upTo) break;
  }
  if (residential && purchasePrice > 3_000_000) {
    tax += (purchasePrice - 3_000_000) * 0.02;
  }
  return Math.round(tax);
}

/** Flat $8,000 exemption for FTHB purchases between $500,001 and $835,000 (Apr 2024+ rules). */
export const BC_FTHB_FLAT_EXEMPTION = 8_000;
export const BC_FTHB_FULL_EXEMPTION_FMV_MAX = 835_000;
export const BC_FTHB_PARTIAL_EXEMPTION_FMV_MAX = 860_000;

/** Exemption amount for a qualifying first-time buyer (not net tax). */
export function calculateBcFthbExemptionAmount(purchasePrice: number): number {
  const grossPtt = calculateBcPropertyTransferTax(purchasePrice);
  if (purchasePrice <= 500_000) {
    return grossPtt;
  }
  if (purchasePrice <= BC_FTHB_FULL_EXEMPTION_FMV_MAX) {
    return BC_FTHB_FLAT_EXEMPTION;
  }
  if (purchasePrice < BC_FTHB_PARTIAL_EXEMPTION_FMV_MAX) {
    const range =
      BC_FTHB_PARTIAL_EXEMPTION_FMV_MAX - BC_FTHB_FULL_EXEMPTION_FMV_MAX;
    const excess = purchasePrice - BC_FTHB_FULL_EXEMPTION_FMV_MAX;
    return Math.round(BC_FTHB_FLAT_EXEMPTION * (1 - excess / range));
  }
  return 0;
}

export function netBcPtt(purchasePrice: number, firstTimeBuyer: boolean): number {
  const gross = calculateBcPropertyTransferTax(purchasePrice);
  if (!firstTimeBuyer) return gross;
  const exemption = calculateBcFthbExemptionAmount(purchasePrice);
  return Math.max(0, gross - exemption);
}

export interface BcClosingCostEstimates {
  pttGross: number;
  netPttFthb: number;
  netPttRepeat: number;
  cmhcPremiumPst: number;
  legalFees: number;
  titleInsurance: number;
  homeInspection: number;
  closingAdjustments: number;
  totalBeyondDownPaymentFthb: number;
  totalBeyondDownPaymentRepeat: number;
}

export function bcClosingCostEstimates(opts: {
  purchasePrice: number;
  cmhcPremium: number;
  cmhcPstRate?: number;
  legalFees?: number;
  titleInsurance?: number;
  homeInspection?: number;
  closingAdjustments?: number;
}): BcClosingCostEstimates {
  const {
    purchasePrice,
    cmhcPremium,
    cmhcPstRate = 0.07,
    legalFees = 1750,
    titleInsurance = 325,
    homeInspection = 500,
    closingAdjustments = 1750,
  } = opts;

  const pttGross = calculateBcPropertyTransferTax(purchasePrice);
  const netPttFthb = netBcPtt(purchasePrice, true);
  const netPttRepeat = netBcPtt(purchasePrice, false);
  const cmhcPremiumPst = Math.round(cmhcPremium * cmhcPstRate);

  const fixedCash =
    cmhcPremiumPst + legalFees + titleInsurance + homeInspection + closingAdjustments;

  return {
    pttGross,
    netPttFthb,
    netPttRepeat,
    cmhcPremiumPst,
    legalFees,
    titleInsurance,
    homeInspection,
    closingAdjustments,
    totalBeyondDownPaymentFthb: netPttFthb + fixedCash,
    totalBeyondDownPaymentRepeat: netPttRepeat + fixedCash,
  };
}
